import React, { useEffect, useState, useCallback } from 'react';
import { api, getApiErrorMessage } from '../lib/api';
import LiveTalkButton from './voice/LiveTalkButton';
import './Karbon.css';

const STRESS_COLORS = {
  calm: '#7ecfc0',
  watch: '#e8c878',
  alert: '#e8a598',
  severe: '#e87878',
};

const READINESS_COLORS = {
  insufficient_evidence: '#e87878',
  early_stage: '#e8c878',
  developing: '#7ecfc0',
  ready_for_validation: '#00e5b0',
};

function StateBadge({ state }) {
  const c = STRESS_COLORS[state?.toLowerCase?.()] || '#7ecfc0';
  return (
    <span className="karbon-state-badge" style={{ borderColor: c, color: c, boxShadow: `0 0 8px ${c}40` }}>
      {state}
    </span>
  );
}

function Chip({ children, color }) {
  return (
    <span className="karbon-chip" style={{ borderColor: `${color}60`, color }}>
      {children}
    </span>
  );
}

export default function KarbonDashboard({ onSwitchTo }) {
  const [state, setState] = useState(null);
  const [aois, setAois] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [standards, setStandards] = useState([]);
  const [selectedAoi, setSelectedAoi] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [selectedStandard, setSelectedStandard] = useState('verra_vm0033');
  const [fieldOps, setFieldOps] = useState(null);
  const [tab, setTab] = useState('candidates'); // candidates | opportunities | evidence | fieldops
  const [error, setError] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [s, a, c, o, st] = await Promise.all([
        api.get('/api/karbon/state'),
        api.get('/api/karbon/aois'),
        api.get('/api/karbon/candidates'),
        api.get('/api/karbon/opportunities'),
        api.get('/api/karbon/standards'),
      ]);
      setState(s.data);
      setAois(a.data.aois || []);
      setCandidates(c.data.candidates || []);
      setOpportunities(o.data.opportunities || []);
      setStandards(st.data.standards || []);
      setError('');
      if (!selectedAoi && a.data.aois?.length) setSelectedAoi(a.data.aois[0].id);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Failed to load Karbon dashboard.'));
      console.error('Karbon fetch failed', e);
    }
  }, [selectedAoi]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!selectedAoi) return;
    Promise.all([
      api.get(`/api/karbon/evidence/${selectedAoi}?standard=${selectedStandard}`),
      api.get(`/api/karbon/field-ops/${selectedAoi}`),
    ]).then(([e, f]) => {
      setEvidence(e.data);
      setFieldOps(f.data);
    }).catch((err) => {
      setError(getApiErrorMessage(err, 'Failed to load Karbon details.'));
    });
  }, [selectedAoi, selectedStandard]);

  const selectedAoiObj = aois.find(a => a.id === selectedAoi);
  const briefing = state?.briefing;

  return (
    <div className="karbon-app" data-testid="karbon-app">
      <div className="karbon-topbar">
        <div className="karbon-brand">
          <span className="karbon-mark" />
          <span className="karbon-name">Karbon</span>
          <span className="karbon-sep">·</span>
          <span className="karbon-role">MANGROVE · BLUE CARBON · MRV</span>
        </div>
        <div className="karbon-top-spacer" />
        {briefing && <StateBadge state={briefing.state} />}
        <span className="karbon-metric">AOIs · {state?.aoi_count ?? '—'}</span>
        <span className="karbon-metric">CANDIDATES · {state?.candidate_count ?? '—'}</span>
        <span className="karbon-metric">OPPORTUNITIES · {state?.opportunity_count ?? '—'}</span>
        <div className="karbon-top-switch">
          {onSwitchTo?.nova && <button onClick={onSwitchTo.nova} className="karbon-nav-btn" data-testid="karbon-to-nova">NOVA</button>}
          {onSwitchTo?.orion && <button onClick={onSwitchTo.orion} className="karbon-nav-btn" data-testid="karbon-to-orion">ORION</button>}
          {onSwitchTo?.vega && <button onClick={onSwitchTo.vega} className="karbon-nav-btn" data-testid="karbon-to-vega">VEGA</button>}
          {onSwitchTo?.atlas && <button onClick={onSwitchTo.atlas} className="karbon-nav-btn" data-testid="karbon-to-atlas">ATLAS</button>}
        </div>
      </div>

      {error && <div className="internal-error">{error}</div>}

      {/* Briefing block with what-changed / why-it-matters / what-to-watch */}
      {briefing && (
        <div className="karbon-briefing" data-testid="karbon-briefing">
          <div className="kb-col">
            <div className="kb-label">SUMMARY</div>
            <div className="kb-text">{briefing.summary}</div>
          </div>
          <div className="kb-col">
            <div className="kb-label kb-label-change">WHAT CHANGED</div>
            <div className="kb-text">{briefing.what_changed}</div>
          </div>
          <div className="kb-col">
            <div className="kb-label kb-label-matters">WHY IT MATTERS</div>
            <div className="kb-text">{briefing.why_it_matters}</div>
          </div>
          <div className="kb-col">
            <div className="kb-label kb-label-watch">WHAT TO WATCH NEXT</div>
            <div className="kb-text">{briefing.what_to_watch_next}</div>
          </div>
        </div>
      )}

      <div className="karbon-body">
        {/* Left rail: AOI list */}
        <div className="karbon-left">
          <div className="karbon-section-header">WATCH ZONES (AOIs)</div>
          <div className="karbon-aoi-list" data-testid="karbon-aoi-list">
            {aois.map(a => {
              const candCount = candidates.filter(c => c.aoi_id === a.id).length;
              const oppCount = opportunities.filter(o => o.aoi_id === a.id).length;
              return (
                <button
                  key={a.id}
                  className={`karbon-aoi-item${selectedAoi === a.id ? ' selected' : ''}`}
                  onClick={() => setSelectedAoi(a.id)}
                  data-testid={`karbon-aoi-${a.id}`}
                >
                  <div className="aoi-row1">
                    <span className="aoi-dot" style={{ background: STRESS_COLORS[a.stress_level] }} />
                    <span className="aoi-name">{a.name}</span>
                    <span className="aoi-state" style={{ color: STRESS_COLORS[a.stress_level] }}>
                      {a.stress_level.toUpperCase()}
                    </span>
                  </div>
                  <div className="aoi-region">{a.country}{a.region ? ` · ${a.region}` : ''}</div>
                  <div className="aoi-row2">
                    <span>{a.baseline_extent_ha.toLocaleString()} ha base</span>
                    <span className={a.change_pct < -1 ? 'aoi-change-neg' : 'aoi-change-ok'}>
                      {a.change_pct >= 0 ? '+' : ''}{a.change_pct.toFixed(2)}%
                    </span>
                  </div>
                  <div className="aoi-stats-row">
                    <span className="aoi-stat-pill" title="Latest mapped extent">
                      <em>NOW</em> {a.latest_extent_ha?.toLocaleString?.() ?? '—'} ha
                    </span>
                    {a.last_sat_pass && (
                      <span className="aoi-stat-pill" title="Last satellite pass">
                        <em>SAT</em> {a.last_sat_pass}
                      </span>
                    )}
                    {a.restoration_potential && (
                      <span className={`aoi-restore-pill restore-${a.restoration_potential.toLowerCase()}`}>
                        RESTORE · {a.restoration_potential.toUpperCase()}
                      </span>
                    )}
                  </div>
                  {(candCount > 0 || oppCount > 0) && (
                    <div className="aoi-counts">
                      {candCount > 0 && <span className="aoi-count cand">{candCount} signal{candCount > 1 ? 's' : ''}</span>}
                      {oppCount > 0 && <span className="aoi-count opp">{oppCount} restore op{oppCount > 1 ? 's' : ''}</span>}
                    </div>
                  )}
                  {a.drivers?.length > 0 && (
                    <div className="aoi-drivers">
                      {a.drivers.slice(0, 3).map(d => <Chip key={d} color="#888">{d.replace(/_/g, ' ')}</Chip>)}
                      {a.drivers.length > 3 && <span className="aoi-driver-more">+{a.drivers.length - 3}</span>}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Center: tabs */}
        <div className="karbon-center">
          <div className="karbon-tabs">
            <button className={tab === 'candidates' ? 'active' : ''} onClick={() => setTab('candidates')} data-testid="karbon-tab-candidates">STRESS CANDIDATES</button>
            <button className={tab === 'opportunities' ? 'active' : ''} onClick={() => setTab('opportunities')} data-testid="karbon-tab-opportunities">RESTORATION</button>
            <button className={tab === 'evidence' ? 'active' : ''} onClick={() => setTab('evidence')} data-testid="karbon-tab-evidence">EVIDENCE (VERRA / GS)</button>
            <button className={tab === 'fieldops' ? 'active' : ''} onClick={() => setTab('fieldops')} data-testid="karbon-tab-fieldops">FIELD OPS</button>
          </div>

          {tab === 'candidates' && (
            <div className="karbon-panel">
              {candidates.filter(c => !selectedAoi || c.aoi_id === selectedAoi).map(c => (
                <div key={c.id} className="karbon-candidate" data-testid={`karbon-candidate-${c.id}`}>
                  <div className="cand-head">
                    <span className="cand-type">{c.candidate_type.replace(/_/g, ' ')}</span>
                    <span className="cand-sev" style={{ color: STRESS_COLORS[c.severity > 0.7 ? 'severe' : c.severity > 0.5 ? 'alert' : c.severity > 0.3 ? 'watch' : 'calm'] }}>
                      SEV {(c.severity * 100).toFixed(0)}%
                    </span>
                    <span className="cand-conf">CONF {(c.confidence * 100).toFixed(0)}%</span>
                  </div>
                  <div className="cand-aoi">{c.aoi_name}</div>
                  <div className="cand-desc">{c.signal_description}</div>
                  <div className="cand-meta">
                    <span className="cand-status">{c.verification_status.replace(/_/g, ' ').toUpperCase()}</span>
                    <span className="cand-baseline">baseline: {c.baseline_ref}</span>
                  </div>
                  <div className="cand-authority">
                    <span className="cand-primary">PRIMARY: {c.primary_authority?.toUpperCase() || 'KARBON'}</span>
                    {c.secondary_authorities?.length > 0 && (
                      <span className="cand-secondary">· SEC: {c.secondary_authorities.map(s => s.toUpperCase()).join(', ')}</span>
                    )}
                  </div>
                  {c.applicable_heuristics?.length > 0 && (
                    <div className="cand-heuristics">
                      {c.applicable_heuristics.map(h => (
                        <div key={h.id} className="heuristic-chip" title={h.id}>
                          <span className="h-tag">RULE</span>
                          <span className="h-body">{h.rule}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {candidates.filter(c => !selectedAoi || c.aoi_id === selectedAoi).length === 0 && (
                <div className="karbon-empty">No candidate signals for this AOI.</div>
              )}
            </div>
          )}

          {tab === 'opportunities' && (
            <div className="karbon-panel">
              {opportunities.filter(o => !selectedAoi || o.aoi_id === selectedAoi).map(o => (
                <div key={o.id} className="karbon-opportunity" data-testid={`karbon-opp-${o.id}`}>
                  <div className="opp-head">
                    <span className="opp-type">{o.opportunity_type.replace(/_/g, ' ').toUpperCase()}</span>
                    <span className="opp-readiness">{o.readiness.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="opp-aoi">{o.aoi_name}</div>
                  <div className="opp-notes">{o.notes}</div>
                  <div className="opp-meta">
                    <span>~{o.estimated_area_ha.toLocaleString()} ha</span>
                    <span>CONF {(o.confidence * 100).toFixed(0)}%</span>
                  </div>
                </div>
              ))}
              {opportunities.filter(o => !selectedAoi || o.aoi_id === selectedAoi).length === 0 && (
                <div className="karbon-empty">No active restoration opportunities for this AOI.</div>
              )}
            </div>
          )}

          {tab === 'evidence' && evidence && (
            <div className="karbon-panel">
              <div className="evidence-std-picker">
                {standards.map(s => (
                  <button
                    key={s.key}
                    className={selectedStandard === s.key ? 'active' : ''}
                    onClick={() => setSelectedStandard(s.key)}
                    data-testid={`karbon-std-${s.key}`}
                  >
                    {s.title}
                  </button>
                ))}
              </div>

              <div className="evidence-header">
                <div className="evidence-title">{evidence.title}</div>
                <div className="evidence-readiness" style={{ color: READINESS_COLORS[evidence.overall_readiness] }}>
                  {evidence.overall_readiness.replace(/_/g, ' ').toUpperCase()} · {(evidence.overall_confidence * 100).toFixed(0)}%
                </div>
              </div>

              {evidence.not_enough_evidence_for?.length > 0 && (
                <div className="evidence-warning">
                  <strong>INSUFFICIENT EVIDENCE FOR:</strong>
                  <ul>
                    {evidence.not_enough_evidence_for.map(x => <li key={x}>{x.replace(/_/g, ' ')}</li>)}
                  </ul>
                </div>
              )}

              <div className="evidence-list">
                {evidence.items.map(item => (
                  <div key={item.key} className={`evidence-item evidence-${item.status}`}>
                    <div className="ev-key-col">
                      <span className={`ev-status-dot status-${item.status}`} />
                      <span className="ev-status-label">{item.status.toUpperCase()}</span>
                    </div>
                    <div className="ev-req">{item.requirement}</div>
                  </div>
                ))}
              </div>

              <div className="evidence-disclaimer">
                Scaffolding only. This tool organizes evidence requirements — it does NOT certify.
                Final validation is by an accredited VVB (Validation/Verification Body).
              </div>
            </div>
          )}

          {tab === 'fieldops' && fieldOps && (
            <div className="karbon-panel">
              <div className="fieldops-header">
                <div className="fo-title">Field Operations — {fieldOps.aoi_name}</div>
                <div className="fo-sub">
                  Tied to candidate types: {fieldOps.candidate_types.map(c => c.replace(/_/g, ' ')).join(', ') || 'general baseline'}
                </div>
              </div>
              <div className="fieldops-grid">
                {Object.entries(fieldOps.items.reduce((acc, item) => {
                  if (!acc[item.category]) acc[item.category] = [];
                  acc[item.category].push(item);
                  return acc;
                }, {})).map(([cat, items]) => (
                  <div key={cat} className="fo-category">
                    <div className="fo-cat-title">{cat}</div>
                    {items.map((it, i) => (
                      <div key={i} className={`fo-item${it.required ? ' fo-required' : ''}`}>
                        <span className="fo-check" />
                        <span className="fo-text">{it.item}</span>
                        {it.required && <span className="fo-req-tag">REQUIRED</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="fieldops-note">{fieldOps.note}</div>
            </div>
          )}
        </div>

        {/* Right rail: AOI detail + briefing meta */}
        <div className="karbon-right">
          {selectedAoiObj && (
            <>
              <div className="karbon-section-header">AOI DETAIL</div>
              <div className="aoi-detail-title">{selectedAoiObj.name}</div>
              <div className="aoi-detail-meta">{selectedAoiObj.country} · {selectedAoiObj.region}</div>
              <div className="aoi-detail-stats">
                <div><span>BASELINE</span><strong>{selectedAoiObj.baseline_extent_ha.toLocaleString()} ha</strong></div>
                <div><span>LATEST</span><strong>{selectedAoiObj.latest_extent_ha.toLocaleString()} ha</strong></div>
                <div><span>Δ</span><strong style={{ color: selectedAoiObj.change_pct < -5 ? '#e87878' : selectedAoiObj.change_pct < -1 ? '#e8c878' : '#7ecfc0' }}>{selectedAoiObj.change_pct >= 0 ? '+' : ''}{selectedAoiObj.change_pct.toFixed(2)}%</strong></div>
                <div><span>RESTORE</span><strong>{selectedAoiObj.restoration_potential.toUpperCase()}</strong></div>
                <div><span>LAST PASS</span><strong>{selectedAoiObj.last_sat_pass || '—'}</strong></div>
              </div>
              {selectedAoiObj.drivers?.length > 0 && (
                <div className="aoi-detail-drivers">
                  <div className="kb-label">DRIVERS</div>
                  {selectedAoiObj.drivers.map(d => <Chip key={d} color="#e8a598">{d.replace(/_/g, ' ')}</Chip>)}
                </div>
              )}
              {selectedAoiObj.notes && <div className="aoi-detail-notes">{selectedAoiObj.notes}</div>}
            </>
          )}

          {briefing && (
            <>
              <div className="karbon-section-header">DISCIPLINED PHRASING</div>
              <div className="karbon-phrases">
                {briefing.disciplined_phrases_used.map(p => (
                  <div key={p} className="karbon-phrase-tag">{p}</div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <LiveTalkButton agent="karbon" label="Talk to Karbon" />
    </div>
  );
}
